import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

interface SerpApiConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (apiKey: string) => void;
  initialApiKey?: string;
}

const SerpApiConfigModal: React.FC<SerpApiConfigModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialApiKey = '',
}) => {
  const { t } = useTranslation();
  const [apiKey, setApiKey] = useState(initialApiKey);
  const [showKey, setShowKey] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(apiKey);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gradient-to-br from-white/80 to-gray-200/80 dark:from-black/80 dark:to-gray-900/80 backdrop-blur-md"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
    >
      <motion.div
        className="relative w-full max-w-md rounded-xl bg-white dark:bg-background-dark border border-gray-200 dark:border-gray-700/50 p-8 shadow-2xl flex flex-col gap-6"
        initial={{ y: 50, opacity: 0, scale: 0.95 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 50, opacity: 0, scale: 0.95 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-1">
            <h2 className="text-2xl font-bold text-[#181411] dark:text-white">{t('integrations.modal_serpapi_title')}</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">{t('integrations.modal_serpapi_subtitle')}</p>
          </div>
          <motion.button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10"
            whileHover={{ rotate: 90 }}
            whileTap={{ scale: 0.9 }}
          >
            <span className="material-symbols-outlined text-gray-500 dark:text-gray-400">close</span>
          </motion.button>
        </div>

        {/* Body / Form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="serpapi-key">
              {t('integrations.modal_serpapi_key_label')}
              <a className="text-primary hover:underline ml-1" href="https://serpapi.com" target="_blank" rel="noopener noreferrer">serpapi.com</a>
            </label>
            <div className="relative">
              <input 
                className="w-full h-12 px-4 bg-white dark:bg-gray-800/30 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-primary focus:border-primary text-sm text-[#181411] dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 pr-10" 
                id="serpapi-key" 
                placeholder={t('integrations.modal_serpapi_key_placeholder')} 
                type={showKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                required
              />
              <motion.button
                type="button"
                onClick={() => setShowKey(!showKey)}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 dark:text-gray-400 hover:text-[#181411] dark:hover:text-white"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <span className="material-symbols-outlined text-xl">
                  {showKey ? 'visibility' : 'visibility_off'}
                </span>
              </motion.button>
            </div>
          </div>

          {/* Footer / Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <motion.button
              type="button"
              onClick={onClose}
              className="flex-1 h-12 flex items-center justify-center rounded-lg bg-gray-200 dark:bg-gray-700 text-sm font-bold text-[#181411] dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {t('modals.cancel')}
            </motion.button>
            <motion.button
              type="submit"
              className="flex-1 h-12 flex items-center justify-center rounded-lg bg-gradient-primary text-white text-sm font-bold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              disabled={!apiKey.trim()}
            >
              {t('sidebar.save_changes')}
            </motion.button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default SerpApiConfigModal;